import React from "react";

import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";

import { formatDateTime, formatRecordedDuration } from "./liveFormat";

import type { JSX } from "react";

import type { LiveSettings } from "../../utils/liveSettings";
import type { LiveView } from "../../utils/liveView";

interface LiveRecapCardProps {
  view: LiveView;
  settings: LiveSettings;
  showSource: boolean;
  showQuality: boolean;
}

interface RecapRowProps {
  label: string;
  children: JSX.Element | string;
}

function RecapRow({ label, children }: RecapRowProps): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

function LiveRecapCard({
  view,
  settings,
  showSource,
  showQuality,
}: LiveRecapCardProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const isStopping = view.state === "stopping";
  const dateTimeFormat = t("liveRegie_dateTimeFormat");
  const opened = formatDateTime(view.openedAt, dateTimeFormat);
  const closed = formatDateTime(view.liveEnd, dateTimeFormat);

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle>{t("liveRegie_recapTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="px-4">
        <dl className="flex flex-col gap-2">
          {showSource ? (
            <RecapRow label={t("liveRegie_source")}>
              {settings.inputType.toUpperCase()}
            </RecapRow>
          ) : null}
          {showQuality ? (
            <RecapRow label={t("liveRegie_maxQuality")}>
              {`${settings.maxResolution}p`}
            </RecapRow>
          ) : null}
          {opened ? (
            <RecapRow label={t("liveRegie_opened")}>{opened}</RecapRow>
          ) : null}
          {isStopping ? (
            <RecapRow label={t("liveRegie_recordedDurationLabel")}>
              <Skeleton className="h-4 w-16" />
            </RecapRow>
          ) : null}
          {!isStopping && closed ? (
            <RecapRow label={t("liveRegie_closed")}>{closed}</RecapRow>
          ) : null}
          {!isStopping && !closed ? (
            <RecapRow label={t("liveRegie_recordedDurationLabel")}>
              {formatRecordedDuration(settings.durationSeconds)}
            </RecapRow>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}

export default LiveRecapCard;
